// 月经周期计算相关函数

export interface CycleData {
  lastPeriodDate: string;
  cycleLength: number;
  periodLength: number;
}

export interface CycleResult {
  nextPeriod: Date;
  ovulationStart: Date;
  ovulationEnd: Date;
  fertileStart: Date;
  fertileEnd: Date;
  safeBeforeStart: Date;
  safeBeforeEnd: Date;
  safeAfterStart: Date;
  safeAfterEnd: Date;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * 验证周期数据
 */
export function validateCycleData(data: CycleData): ValidationResult {
  const errors: string[] = [];

  // 验证末次月经日期
  if (!data.lastPeriodDate) {
    errors.push('请选择末次月经开始日期');
  } else {
    const lastPeriod = new Date(data.lastPeriodDate);
    const today = new Date();
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() - 6); // 最多6个月前

    if (lastPeriod > today) {
      errors.push('末次月经日期不能是未来日期');
    } else if (lastPeriod < maxDate) {
      errors.push('末次月经日期不能超过6个月前');
    }
  }

  // 验证周期长度
  if (!data.cycleLength || data.cycleLength < 21 || data.cycleLength > 35) {
    errors.push('月经周期长度应在21-35天之间');
  }

  // 验证月经持续时间
  if (!data.periodLength || data.periodLength < 3 || data.periodLength > 7) {
    errors.push('月经持续时间应在3-7天之间');
  }

  // 验证逻辑关系
  if (data.periodLength >= data.cycleLength) {
    errors.push('月经持续时间不能大于或等于周期长度');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 计算月经周期相关日期
 */
export function calculateCycle(data: CycleData): CycleResult {
  const lastPeriod = new Date(data.lastPeriodDate);
  const { cycleLength, periodLength } = data;

  // 计算下次月经日期
  const nextPeriod = new Date(lastPeriod);
  nextPeriod.setDate(nextPeriod.getDate() + cycleLength);

  // 计算排卵期（周期长度-14天，持续2天）
  const ovulationDay = cycleLength - 14;
  const ovulationStart = new Date(lastPeriod);
  ovulationStart.setDate(ovulationStart.getDate() + ovulationDay - 1);
  
  const ovulationEnd = new Date(ovulationStart);
  ovulationEnd.setDate(ovulationEnd.getDate() + 1);

  // 计算易孕期（排卵期前5天到排卵期后1天）
  const fertileStart = new Date(ovulationStart);
  fertileStart.setDate(fertileStart.getDate() - 5);
  
  const fertileEnd = new Date(ovulationEnd);
  fertileEnd.setDate(fertileEnd.getDate() + 1);

  // 计算安全期
  // 月经后安全期：月经结束后到易孕期开始前
  const safeAfterStart = new Date(lastPeriod);
  safeAfterStart.setDate(safeAfterStart.getDate() + periodLength);
  
  const safeAfterEnd = new Date(fertileStart);
  safeAfterEnd.setDate(safeAfterEnd.getDate() - 1);

  // 月经前安全期：易孕期结束后到下次月经开始前
  const safeBeforeStart = new Date(fertileEnd);
  safeBeforeStart.setDate(safeBeforeStart.getDate() + 1);
  
  const safeBeforeEnd = new Date(nextPeriod);
  safeBeforeEnd.setDate(safeBeforeEnd.getDate() - 1);

  return {
    nextPeriod,
    ovulationStart,
    ovulationEnd,
    fertileStart,
    fertileEnd,
    safeBeforeStart,
    safeBeforeEnd,
    safeAfterStart,
    safeAfterEnd
  };
}

/**
 * 获取当前周期状态
 */
export function getCurrentCycleStatus(data: CycleData): {
  status: 'menstrual' | 'safe' | 'fertile' | 'ovulation';
  description: string;
  daysUntilNext: number;
} {
  const today = new Date();
  const result = calculateCycle(data);
  
  // 检查是否在月经期
  const lastPeriod = new Date(data.lastPeriodDate);
  const periodEnd = new Date(lastPeriod);
  periodEnd.setDate(periodEnd.getDate() + data.periodLength - 1);
  
  if (today >= lastPeriod && today <= periodEnd) {
    const daysLeft = Math.ceil((periodEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return {
      status: 'menstrual',
      description: `月经期第${Math.ceil((today.getTime() - lastPeriod.getTime()) / (1000 * 60 * 60 * 24)) + 1}天`,
      daysUntilNext: daysLeft
    };
  }
  
  // 检查是否在排卵期
  if (today >= result.ovulationStart && today <= result.ovulationEnd) {
    const daysLeft = Math.ceil((result.ovulationEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return {
      status: 'ovulation',
      description: '排卵期',
      daysUntilNext: daysLeft
    };
  }
  
  // 检查是否在易孕期
  if (today >= result.fertileStart && today <= result.fertileEnd) {
    const daysLeft = Math.ceil((result.fertileEnd.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return {
      status: 'fertile',
      description: '易孕期',
      daysUntilNext: daysLeft
    };
  }
  
  // 其他时间为安全期
  let nextEventDate: Date;
  let nextEventName: string;
  
  if (today < result.fertileStart) {
    nextEventDate = result.fertileStart;
    nextEventName = '易孕期';
  } else {
    nextEventDate = result.nextPeriod;
    nextEventName = '下次月经';
  }
  
  const daysUntilNext = Math.ceil((nextEventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  return {
    status: 'safe',
    description: '安全期',
    daysUntilNext
  };
}

/**
 * 格式化日期为中文
 */
export function formatDateChinese(date: Date): string {
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * 计算两个日期之间的天数
 */
export function daysBetween(date1: Date, date2: Date): number {
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * 获取周期阶段的颜色
 */
export function getPhaseColor(phase: 'menstrual' | 'safe' | 'fertile' | 'ovulation'): string {
  switch (phase) {
    case 'menstrual':
      return '#ef4444'; // 红色
    case 'fertile':
      return '#10b981'; // 绿色
    case 'ovulation':
      return '#8b5cf6'; // 紫色
    case 'safe':
      default:
      return '#3b82f6'; // 蓝色
  }
}